<template>
  <div class="report-center-container">
    <el-card class="filter-container" shadow="never">
      <div slot="header" class="clearfix">
        <span>{{ $t('reportCenter.title') }}</span>
      </div>
      
      <el-form :model="reportForm" ref="reportForm" label-width="120px" size="small">
        <el-form-item :label="$t('reportCenter.reportType')" prop="type">
          <el-select v-model="reportForm.type" :placeholder="$t('common.pleaseSelect') + $t('reportCenter.reportType')" style="width: 250px;">
            <el-option v-for="item in reportTypes" :key="item.value" :label="item.label" :value="item.value"></el-option>
          </el-select>
        </el-form-item>
        
        <el-form-item :label="$t('reportCenter.statisticsPeriod')" prop="period">
          <el-select v-model="reportForm.period" :placeholder="$t('common.pleaseSelect') + $t('reportCenter.statisticsPeriod')" style="width: 250px;">
            <el-option v-for="item in periodOptions" :key="item.value" :label="item.label" :value="item.value"></el-option>
          </el-select>
        </el-form-item>
        
        <el-form-item :label="$t('reportCenter.dateRange')" prop="dateRange">
          <el-date-picker
            v-model="reportForm.dateRange"
            type="daterange"
            align="right"
            :range-separator="$t('reportCenter.to')"
            :start-placeholder="$t('reportCenter.startDate')"
            :end-placeholder="$t('reportCenter.endDate')"
            :picker-options="pickerOptions"
            format="yyyy-MM-dd"
            value-format="yyyy-MM-dd"
            style="width: 350px;">
          </el-date-picker>
        </el-form-item>
        
        <el-form-item :label="$t('reportCenter.groupBy')" prop="groupBy" v-if="showGroupByOption">
          <el-select v-model="reportForm.groupBy" :placeholder="$t('common.pleaseSelect') + $t('reportCenter.groupBy')" style="width: 250px;">
            <el-option v-for="item in groupByOptions" :key="item.value" :label="item.label" :value="item.value"></el-option>
          </el-select>
        </el-form-item>
        
        <el-form-item :label="$t('reportCenter.dataDetail')" prop="showDetail">
          <el-switch v-model="reportForm.showDetail"></el-switch>
          <span class="description">{{ $t('reportCenter.showDetailData') }}</span>
        </el-form-item>
        
        <el-form-item :label="$t('reportCenter.chartDisplay')" prop="showChart">
          <el-switch v-model="reportForm.showChart"></el-switch>
          <span class="description">{{ $t('reportCenter.includeChart') }}</span>
        </el-form-item>
        
        <el-form-item>
          <el-button type="primary" @click="generateReport">{{ $t('reportCenter.generateReport') }}</el-button>
          <el-button type="success" @click="exportReport" :disabled="!reportGenerated">{{ $t('reportCenter.exportExcel') }}</el-button>
          <el-button @click="resetForm">{{ $t('common.reset') }}</el-button>
        </el-form-item>
      </el-form>
    </el-card>
    
    <el-card v-if="reportGenerated" style="margin-top: 20px;">
      <div slot="header" class="clearfix">
        <span>{{ reportTitle }}</span>
        <el-button style="float: right; padding: 3px 0" type="text" @click="exportReport">
          <i class="el-icon-download"></i> {{ $t('common.export') }}
        </el-button>
      </div>
      
      <!-- 报表显示区域 -->
      <div class="report-content">
        <!-- 图表区域 -->
        <div v-if="reportForm.showChart" class="chart-container">
          <div ref="reportChart" style="width: 100%; height: 350px;"></div>
        </div>
        
        <!-- 数据表格区域 -->
        <div class="table-container">
          <el-table
            :data="reportData"
            style="width: 100%"
            border
            max-height="500">
            <el-table-column
              v-for="(column, index) in reportColumns"
              :key="index"
              :prop="column.prop"
              :label="column.label"
              :width="column.width">
              <template slot-scope="scope" v-if="column.formatter">
                {{ column.formatter(scope.row) }}
              </template>
            </el-table-column>
          </el-table>
          
          <div class="pagination-container">
            <el-pagination
              @size-change="handleSizeChange"
              @current-change="handleCurrentChange"
              :current-page="currentPage"
              :page-sizes="[10, 20, 50, 100]"
              :page-size="pageSize"
              layout="total, sizes, prev, pager, next, jumper"
              :total="totalItems">
            </el-pagination>
          </div>
        </div>
      </div>
    </el-card>
    
    <!-- 历史报表区域 -->
    <el-card style="margin-top: 20px;">
      <div slot="header" class="clearfix">
        <span>{{ $t('reportCenter.historyReports') }}</span>
      </div>
      
      <el-table
        :data="historyReports"
        style="width: 100%"
        border>
        <el-table-column prop="title" :label="$t('reportCenter.reportName')" width="250"></el-table-column>
        <el-table-column prop="type" :label="$t('reportCenter.reportType')" width="120">
          <template slot-scope="scope">
            {{ getReportTypeLabel(scope.row.type) }}
          </template>
        </el-table-column>
        <el-table-column prop="dateRange" :label="$t('reportCenter.statisticsTime')" width="220"></el-table-column>
        <el-table-column prop="createTime" :label="$t('reportCenter.generateTime')" width="180"></el-table-column>
        <el-table-column prop="creator" :label="$t('reportCenter.generator')" width="120"></el-table-column>
        <el-table-column :label="$t('common.operation')" width="180">
          <template slot-scope="scope">
            <el-button type="text" size="small" @click="viewHistoryReport(scope.row)">{{ $t('reportCenter.view') }}</el-button>
            <el-button type="text" size="small" @click="downloadHistoryReport(scope.row)">{{ $t('reportCenter.download') }}</el-button>
            <el-button type="text" size="small" style="color: #F56C6C;" @click="deleteHistoryReport(scope.row)">{{ $t('common.delete') }}</el-button>
          </template>
        </el-table-column>
      </el-table>
    </el-card>
  </div>
</template>

<script>
import * as echarts from 'echarts'

export default {
  name: 'ReportCenter',
  data() {
    return {
      pickerOptions: {
        shortcuts: []
      },
      reportForm: {
        type: 'member_statistics',
        period: 'day',
        dateRange: [
          this.formatDate(new Date(new Date().getTime() - 30 * 24 * 60 * 60 * 1000)),
          this.formatDate(new Date())
        ],
        groupBy: 'gender',
        showDetail: true,
        showChart: true
      },
      reportGenerated: false,
      reportTitle: '',
      reportChart: null,
      reportData: [],
      reportColumns: [],
      currentPage: 1,
      pageSize: 10,
      totalItems: 0,
      historyReports: [
        {
          id: 1,
          title: '2023年4月会员增长报表',
          type: 'member_statistics',
          dateRange: '2023-04-01 至 2023-04-30',
          createTime: '2023-05-01 09:30:22',
          creator: '管理员'
        },
        {
          id: 2,
          title: '2023年1季度收入统计报表',
          type: 'finance_income',
          dateRange: '2023-01-01 至 2023-03-31',
          createTime: '2023-04-02 15:22:36',
          creator: '管理员'
        },
        {
          id: 3,
          title: '2023年3月教练绩效报表',
          type: 'trainer_performance',
          dateRange: '2023-03-01 至 2023-03-31',
          createTime: '2023-04-01 16:15:47',
          creator: '管理员'
        }
      ]
    }
  },
  computed: {
    showGroupByOption() {
      return this.reportForm.type === 'member_statistics'
    },
    reportTypes() {
      return [
        { label: this.$t('reportCenter.types.memberStatistics'), value: 'member_statistics' },
        { label: this.$t('reportCenter.types.courseBooking'), value: 'course_booking' },
        { label: this.$t('reportCenter.types.trainerPerformance'), value: 'trainer_performance' },
        { label: this.$t('reportCenter.types.financeIncome'), value: 'finance_income' },
        { label: this.$t('reportCenter.types.financeExpense'), value: 'finance_expense' },
        { label: this.$t('reportCenter.types.financeSummary'), value: 'finance_summary' }
      ]
    },
    periodOptions() {
      return [
        { label: this.$t('reportCenter.periods.day'), value: 'day' },
        { label: this.$t('reportCenter.periods.week'), value: 'week' },
        { label: this.$t('reportCenter.periods.month'), value: 'month' },
        { label: this.$t('reportCenter.periods.quarter'), value: 'quarter' },
        { label: this.$t('reportCenter.periods.year'), value: 'year' }
      ]
    },
    groupByOptions() {
      return [
        { label: this.$t('reportCenter.groupByOptions.gender'), value: 'gender' },
        { label: this.$t('reportCenter.groupByOptions.ageGroup'), value: 'age_group' },
        { label: this.$t('reportCenter.groupByOptions.memberLevel'), value: 'member_level' },
        { label: this.$t('reportCenter.groupByOptions.region'), value: 'region' }
      ]
    }
  },
  created() {
    // 初始化快捷方式（需要在组件创建后才能访问 $t）
    this.pickerOptions.shortcuts = [
      {
        text: this.$t('reportCenter.shortcuts.lastWeek'),
        onClick: (picker) => {
          const end = new Date()
          const start = new Date()
          start.setTime(start.getTime() - 3600 * 1000 * 24 * 7)
          picker.$emit('pick', [start, end])
        }
      },
      {
        text: this.$t('reportCenter.shortcuts.lastMonth'),
        onClick: (picker) => {
          const end = new Date()
          const start = new Date()
          start.setTime(start.getTime() - 3600 * 1000 * 24 * 30)
          picker.$emit('pick', [start, end])
        }
      },
      {
        text: this.$t('reportCenter.shortcuts.lastThreeMonths'),
        onClick: (picker) => {
          const end = new Date()
          const start = new Date()
          start.setTime(start.getTime() - 3600 * 1000 * 24 * 90)
          picker.$emit('pick', [start, end])
        }
      }
    ]
  },
  methods: {
    formatDate(date) {
      const year = date.getFullYear()
      const month = (date.getMonth() + 1).toString().padStart(2, '0')
      const day = date.getDate().toString().padStart(2, '0')
      return `${year}-${month}-${day}`
    },
    generateReport() {
      // 这里应该调用后端API生成报表数据
      // 模拟后端返回的数据
      this.reportGenerated = true
      
      // 根据报表类型设置标题
      const typeLabel = this.getReportTypeLabel(this.reportForm.type)
      const startDate = this.reportForm.dateRange[0]
      const endDate = this.reportForm.dateRange[1]
      this.reportTitle = `${typeLabel}（${startDate} 至 ${endDate}）`
      
      // 设置不同报表类型的表格列
      this.setReportColumns()
      
      // 生成模拟数据
      this.generateMockData()
      
      // 初始化图表
      if (this.reportForm.showChart) {
        this.$nextTick(() => {
          this.initReportChart()
        })
      }
    },
    getReportTypeLabel(type) {
      const found = this.reportTypes.find(item => item.value === type)
      return found ? found.label : type
    },
    setReportColumns() {
      switch (this.reportForm.type) {
        case 'member_statistics':
          this.reportColumns = [
            { prop: 'date', label: this.$t('reportCenter.columns.date'), width: '120' },
            { prop: 'newMembers', label: this.$t('reportCenter.columns.newMembers'), width: '100' },
            { prop: 'activeMembers', label: this.$t('reportCenter.columns.activeMembers'), width: '100' },
            { prop: 'totalMembers', label: this.$t('reportCenter.columns.totalMembers'), width: '100' },
            { prop: 'maleCount', label: this.$t('reportCenter.columns.maleCount'), width: '100' },
            { prop: 'femaleCount', label: this.$t('reportCenter.columns.femaleCount'), width: '100' },
            { prop: 'otherCount', label: this.$t('reportCenter.columns.otherCount'), width: '100' },
            { prop: 'activeRate', label: this.$t('reportCenter.columns.activeRate'), width: '100', formatter: row => `${row.activeRate}%` }
          ]
          break
        case 'course_booking':
          this.reportColumns = [
            { prop: 'date', label: this.$t('reportCenter.columns.date'), width: '120' },
            { prop: 'courseType', label: this.$t('reportCenter.columns.courseType'), width: '120' },
            { prop: 'courseName', label: this.$t('reportCenter.columns.courseName'), width: '180' },
            { prop: 'bookingCount', label: this.$t('reportCenter.columns.bookingCount'), width: '100' },
            { prop: 'capacity', label: this.$t('reportCenter.columns.capacity'), width: '100' },
            { prop: 'bookingRate', label: this.$t('reportCenter.columns.bookingRate'), width: '100', formatter: row => `${row.bookingRate}%` },
            { prop: 'attendCount', label: this.$t('reportCenter.columns.attendCount'), width: '120' },
            { prop: 'attendRate', label: this.$t('reportCenter.columns.attendRate'), width: '100', formatter: row => `${row.attendRate}%` }
          ]
          break
        case 'trainer_performance':
          this.reportColumns = [
            { prop: 'trainerName', label: this.$t('reportCenter.columns.trainerName'), width: '120' },
            { prop: 'courseCount', label: this.$t('reportCenter.columns.courseCount'), width: '100' },
            { prop: 'totalHours', label: this.$t('reportCenter.columns.totalHours'), width: '120' },
            { prop: 'studentCount', label: this.$t('reportCenter.columns.studentCount'), width: '100' },
            { prop: 'bookingRate', label: this.$t('reportCenter.columns.bookingRate'), width: '120', formatter: row => `${row.bookingRate}%` },
            { prop: 'averageRating', label: this.$t('reportCenter.columns.averageRating'), width: '100' },
            { prop: 'income', label: this.$t('reportCenter.columns.income'), width: '120', formatter: row => `¥${row.income.toFixed(2)}` }
          ]
          break
        case 'finance_income':
        case 'finance_expense':
        case 'finance_summary':
          this.reportColumns = [
            { prop: 'date', label: this.$t('reportCenter.columns.date'), width: '120' },
            { prop: 'membershipIncome', label: this.$t('reportCenter.columns.membershipIncome'), width: '120', formatter: row => `¥${row.membershipIncome.toFixed(2)}` },
            { prop: 'courseIncome', label: this.$t('reportCenter.columns.courseIncome'), width: '120', formatter: row => `¥${row.courseIncome.toFixed(2)}` },
            { prop: 'otherIncome', label: this.$t('reportCenter.columns.otherIncome'), width: '120', formatter: row => `¥${row.otherIncome.toFixed(2)}` },
            { prop: 'totalIncome', label: this.$t('reportCenter.columns.totalIncome'), width: '120', formatter: row => `¥${row.totalIncome.toFixed(2)}` },
            { prop: 'operationExpense', label: this.$t('reportCenter.columns.operationExpense'), width: '120', formatter: row => `¥${row.operationExpense.toFixed(2)}` },
            { prop: 'salaryExpense', label: this.$t('reportCenter.columns.salaryExpense'), width: '120', formatter: row => `¥${row.salaryExpense.toFixed(2)}` },
            { prop: 'otherExpense', label: this.$t('reportCenter.columns.otherExpense'), width: '120', formatter: row => `¥${row.otherExpense.toFixed(2)}` },
            { prop: 'totalExpense', label: this.$t('reportCenter.columns.totalExpense'), width: '120', formatter: row => `¥${row.totalExpense.toFixed(2)}` },
            { prop: 'netIncome', label: this.$t('reportCenter.columns.netIncome'), width: '120', formatter: row => `¥${row.netIncome.toFixed(2)}` }
          ]
          break
      }
    },
    generateMockData() {
      // 生成模拟报表数据
      this.reportData = []
      this.totalItems = 30
      
      for (let i = 0; i < this.totalItems; i++) {
        let row = {}
        
        switch (this.reportForm.type) {
          case 'member_statistics':
            row = this.generateMemberStatisticsRow(i)
            break
          case 'course_booking':
            row = this.generateCourseBookingRow(i)
            break
          case 'trainer_performance':
            row = this.generateTrainerPerformanceRow(i)
            break
          case 'finance_income':
          case 'finance_expense':
          case 'finance_summary':
            row = this.generateFinanceRow(i)
            break
        }
        
        this.reportData.push(row)
      }
    },
    generateMemberStatisticsRow(index) {
      const date = new Date()
      date.setDate(date.getDate() - index)
      const formattedDate = this.formatDate(date)
      
      const newMembers = Math.floor(Math.random() * 10) + 1
      const activeMembers = Math.floor(Math.random() * 50) + 30
      const totalMembers = 200 + Math.floor(index / 2) * 10
      const maleCount = Math.floor(totalMembers * 0.6)
      const femaleCount = Math.floor(totalMembers * 0.35)
      const otherCount = totalMembers - maleCount - femaleCount
      const activeRate = ((activeMembers / totalMembers) * 100).toFixed(2)
      
      return {
        date: formattedDate,
        newMembers,
        activeMembers,
        totalMembers,
        maleCount,
        femaleCount,
        otherCount,
        activeRate
      }
    },
    generateCourseBookingRow(index) {
      const courseTypes = ['瑜伽', '力量', '有氧', '普拉提', '舞蹈']
      const courseNames = [
        '初级瑜伽', '高级瑜伽', '力量训练', '有氧操', '普拉提入门',
        '街舞', '肚皮舞', 'TRX训练', '哑铃训练', 'HIIT'
      ]
      
      const date = new Date()
      date.setDate(date.getDate() - index % 7)
      const formattedDate = this.formatDate(date)
      
      const courseType = courseTypes[index % courseTypes.length]
      const courseName = courseNames[index % courseNames.length]
      const capacity = 20
      const bookingCount = Math.floor(Math.random() * capacity) + 1
      const bookingRate = ((bookingCount / capacity) * 100).toFixed(2)
      const attendCount = Math.floor(bookingCount * (0.8 + Math.random() * 0.2))
      const attendRate = ((attendCount / bookingCount) * 100).toFixed(2)
      
      return {
        date: formattedDate,
        courseType,
        courseName,
        bookingCount,
        capacity,
        bookingRate,
        attendCount,
        attendRate
      }
    },
    generateTrainerPerformanceRow(index) {
      const trainerNames = ['张教练', '李教练', '王教练', '赵教练', '刘教练']
      
      const trainerName = trainerNames[index % trainerNames.length]
      const courseCount = Math.floor(Math.random() * 10) + 10
      const totalHours = courseCount * 1.5
      const studentCount = courseCount * (Math.floor(Math.random() * 10) + 5)
      const bookingRate = (60 + Math.floor(Math.random() * 40)).toFixed(2)
      const averageRating = (3.5 + Math.random() * 1.5).toFixed(1)
      const income = courseCount * (Math.floor(Math.random() * 1000) + 500)
      
      return {
        trainerName,
        courseCount,
        totalHours,
        studentCount,
        bookingRate,
        averageRating,
        income
      }
    },
    generateFinanceRow(index) {
      const date = new Date()
      date.setDate(date.getDate() - index)
      const formattedDate = this.formatDate(date)
      
      const membershipIncome = Math.floor(Math.random() * 5000) + 1000
      const courseIncome = Math.floor(Math.random() * 8000) + 2000
      const otherIncome = Math.floor(Math.random() * 1000)
      const totalIncome = membershipIncome + courseIncome + otherIncome
      
      const operationExpense = Math.floor(Math.random() * 3000) + 1000
      const salaryExpense = Math.floor(Math.random() * 5000) + 3000
      const otherExpense = Math.floor(Math.random() * 1000)
      const totalExpense = operationExpense + salaryExpense + otherExpense
      
      const netIncome = totalIncome - totalExpense
      
      return {
        date: formattedDate,
        membershipIncome,
        courseIncome,
        otherIncome,
        totalIncome,
        operationExpense,
        salaryExpense,
        otherExpense,
        totalExpense,
        netIncome
      }
    },
    initReportChart() {
      if (this.reportChart) {
        this.reportChart.dispose()
      }
      
      this.reportChart = echarts.init(this.$refs.reportChart)
      
      // 根据报表类型设置不同的图表
      let option = {}
      
      switch (this.reportForm.type) {
        case 'member_statistics':
          option = this.getMemberStatisticsChartOption()
          break
        case 'course_booking':
          option = this.getCourseBookingChartOption()
          break
        case 'trainer_performance':
          option = this.getTrainerPerformanceChartOption()
          break
        case 'finance_income':
        case 'finance_expense':
        case 'finance_summary':
          option = this.getFinanceChartOption()
          break
      }
      
      this.reportChart.setOption(option)
    },
    getMemberStatisticsChartOption() {
      const dates = this.reportData.map(item => item.date).reverse()
      const newMembersData = this.reportData.map(item => item.newMembers).reverse()
      const activeMembersData = this.reportData.map(item => item.activeMembers).reverse()
      const totalMembersData = this.reportData.map(item => item.totalMembers).reverse()
      
      return {
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'shadow'
          }
        },
        legend: {
          data: [this.$t('reportCenter.columns.newMembers'), this.$t('reportCenter.columns.activeMembers'), this.$t('reportCenter.columns.totalMembers')]
        },
        grid: {
          left: '3%',
          right: '4%',
          bottom: '3%',
          containLabel: true
        },
        xAxis: {
          type: 'category',
          data: dates
        },
        yAxis: [
          {
            type: 'value',
            name: this.$t('reportCenter.chart.memberCount'),
            position: 'left'
          },
          {
            type: 'value',
            name: this.$t('reportCenter.chart.accumulatedMembers'),
            position: 'right'
          }
        ],
        series: [
          {
            name: this.$t('reportCenter.columns.newMembers'),
            type: 'bar',
            data: newMembersData
          },
          {
            name: this.$t('reportCenter.columns.activeMembers'),
            type: 'bar',
            data: activeMembersData
          },
          {
            name: this.$t('reportCenter.columns.totalMembers'),
            type: 'line',
            yAxisIndex: 1,
            data: totalMembersData
          }
        ]
      }
    },
    getCourseBookingChartOption() {
      const courseTypes = [...new Set(this.reportData.map(item => item.courseType))]
      const courseData = courseTypes.map(type => {
        const filteredData = this.reportData.filter(item => item.courseType === type)
        return {
          name: type,
          value: filteredData.reduce((sum, item) => sum + item.bookingCount, 0)
        }
      })
      
      return {
        tooltip: {
          trigger: 'item',
          formatter: '{a} <br/>{b}: {c} ({d}%)'
        },
        legend: {
          orient: 'vertical',
          right: 10,
          top: 'center',
          data: courseTypes
        },
        series: [
          {
            name: this.$t('reportCenter.chart.courseBooking'),
            type: 'pie',
            radius: ['50%', '70%'],
            avoidLabelOverlap: false,
            label: {
              show: false,
              position: 'center'
            },
            emphasis: {
              label: {
                show: true,
                fontSize: '16',
                fontWeight: 'bold'
              }
            },
            labelLine: {
              show: false
            },
            data: courseData
          }
        ]
      }
    },
    getTrainerPerformanceChartOption() {
      const trainers = this.reportData.map(item => item.trainerName)
      const courseCountData = this.reportData.map(item => item.courseCount)
      const studentCountData = this.reportData.map(item => item.studentCount)
      const incomeData = this.reportData.map(item => item.income)
      
      return {
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'shadow'
          }
        },
        legend: {
          data: [this.$t('reportCenter.columns.courseCount'), this.$t('reportCenter.columns.studentCount'), this.$t('reportCenter.columns.income')]
        },
        grid: {
          left: '3%',
          right: '4%',
          bottom: '3%',
          containLabel: true
        },
        xAxis: {
          type: 'category',
          data: trainers
        },
        yAxis: [
          {
            type: 'value',
            name: this.$t('reportCenter.chart.count'),
            position: 'left'
          },
          {
            type: 'value',
            name: this.$t('reportCenter.chart.amount'),
            position: 'right'
          }
        ],
        series: [
          {
            name: this.$t('reportCenter.columns.courseCount'),
            type: 'bar',
            data: courseCountData
          },
          {
            name: this.$t('reportCenter.columns.studentCount'),
            type: 'bar',
            data: studentCountData
          },
          {
            name: this.$t('reportCenter.columns.income'),
            type: 'line',
            yAxisIndex: 1,
            data: incomeData
          }
        ]
      }
    },
    getFinanceChartOption() {
      const dates = this.reportData.map(item => item.date).reverse()
      const incomeData = this.reportData.map(item => item.totalIncome).reverse()
      const expenseData = this.reportData.map(item => item.totalExpense).reverse()
      const netIncomeData = this.reportData.map(item => item.netIncome).reverse()
      
      return {
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'line'
          }
        },
        legend: {
          data: [this.$t('reportCenter.chart.revenue'), this.$t('reportCenter.chart.expense'), this.$t('reportCenter.columns.netIncome')]
        },
        grid: {
          left: '3%',
          right: '4%',
          bottom: '3%',
          containLabel: true
        },
        xAxis: {
          type: 'category',
          data: dates
        },
        yAxis: {
          type: 'value',
          name: this.$t('reportCenter.chart.amount')
        },
        series: [
          {
            name: this.$t('reportCenter.chart.revenue'),
            type: 'bar',
            stack: 'total',
            data: incomeData
          },
          {
            name: this.$t('reportCenter.chart.expense'),
            type: 'bar',
            stack: 'total',
            data: expenseData.map(value => -value)
          },
          {
            name: this.$t('reportCenter.columns.netIncome'),
            type: 'line',
            data: netIncomeData
          }
        ]
      }
    },
    exportReport() {
      // 调用导出接口，这里只做提示
      this.$message({
        message: this.$t('reportCenter.exportSuccess'),
        type: 'success'
      })
    },
    resetForm() {
      this.$refs.reportForm.resetFields()
      this.reportGenerated = false
    },
    handleSizeChange(val) {
      this.pageSize = val
    },
    handleCurrentChange(val) {
      this.currentPage = val
    },
    viewHistoryReport(report) {
      // 查看历史报表，这里只做提示
      this.$message({
        message: this.$t('reportCenter.viewingReport', { title: report.title }),
        type: 'info'
      })
    },
    downloadHistoryReport(report) {
      // 下载历史报表，这里只做提示
      this.$message({
        message: this.$t('reportCenter.downloadingReport', { title: report.title }),
        type: 'success'
      })
    },
    deleteHistoryReport(report) {
      this.$confirm(
        this.$t('reportCenter.confirmDeleteReport', { title: report.title }), 
        this.$t('common.tip'), 
        {
          confirmButtonText: this.$t('common.confirm'),
          cancelButtonText: this.$t('common.cancel'),
          type: 'warning'
        }
      ).then(() => {
        // 从列表中删除该报表
        const index = this.historyReports.findIndex(item => item.id === report.id)
        if (index !== -1) {
          this.historyReports.splice(index, 1)
        }
        
        this.$message({
          type: 'success',
          message: this.$t('reportCenter.deleteSuccess')
        })
      }).catch(() => {
        // 用户取消操作
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.report-center-container {
  padding: 20px;
  
  .filter-container {
    margin-bottom: 20px;
  }
  
  .description {
    margin-left: 10px;
    color: #909399;
    font-size: 12px;
  }
  
  .report-content {
    .chart-container {
      width: 100%;
      height: 350px;
      margin-bottom: 20px;
    }
    
    .table-container {
      margin-top: 20px;
      
      .pagination-container {
        margin-top: 20px;
        text-align: right;
      }
    }
  }
}
</style> 